import { Form, type FormInstance, Select } from 'antd';
import { useCallback, useMemo } from 'react';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  SearchQuery,
  searchPageNoAtom,
  searchPageSizeAtom,
  searchQueryAtom,
} from 'packages/omnitag/state';
import { searchQueryToQueryParams } from 'packages/omnitag/text-search/search-top-panel';
import { OmnitagFilter } from 'packages/lib/api/omnitag/contract/omnitag-filters';
import { FilterItem } from 'packages/omnitag/text-search/filter-item';
import { useAtomValue } from 'jotai';

// Oct-30-2025 - Used Gemini 2.5 Agent to add support for 'DateRange' filters

dayjs.extend(utc); // UTC is a plugin that doesn't come with dayjs by default

export type SearchQueryForm = Omit<SearchQuery, 'text'>;

export const FiltersForm = ({
  defaultSearchTerm,
  form,
  dynamicFilters,
  onClose,
}: {
  defaultSearchTerm: SearchQuery;
  form: FormInstance<SearchQueryForm | Record<string, unknown>>;
  dynamicFilters: OmnitagFilter[];
  onClose: () => void;
}): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch();
  const searchQuery = useAtomValue(searchQueryAtom);
  const pageNo = useAtomValue(searchPageNoAtom);
  const pageSize = useAtomValue(searchPageSizeAtom);

  const defaultSearch = useMemo<
    SearchQueryForm | Record<string, unknown>
  >(() => {
    const { logNames } = defaultSearchTerm as Partial<SearchQuery>;

    const result: Record<string, unknown> = {
      logNames,
    };

    for (const filter of dynamicFilters) {
      if (filter.filterType === 'select') {
        result[filter.paramName] = defaultSearchTerm[filter.paramName] || [];
      } else if (filter.filterType === 'text') {
        result[filter.paramName] = defaultSearchTerm[filter.paramName] || '';
      } else if (filter.filterType === 'number') {
        result[filter.paramName] =
          defaultSearchTerm[filter.paramName] ?? filter.defaultValue ?? null;
      } else if (filter.filterType === 'toggle') {
        result[filter.paramName] =
          defaultSearchTerm[filter.paramName] ?? filter.defaultValue ?? false;
      } else if (filter.filterType === 'dateRange') {
        const from = defaultSearchTerm[`${filter.paramName}From`];
        const to = defaultSearchTerm[`${filter.paramName}To`];
        result[filter.paramName] =
          from && to ? [dayjs(from).utc(), dayjs(to).utc()] : null;
      }
    }

    return result as SearchQueryForm;
  }, [defaultSearchTerm, dynamicFilters]);

  const submit = useCallback(
    async (
      formValues: SearchQueryForm | Record<string, unknown>
    ): Promise<void> => {
      const obj: Record<string, unknown> = {
        ...searchQuery,
        logNames: formValues.logNames,
      };

      for (const filter of dynamicFilters) {
        if (filter.filterType === 'select') {
          obj[filter.paramName] = formValues[filter.paramName] || [];
        } else if (filter.filterType === 'text') {
          obj[filter.paramName] = formValues[filter.paramName] || '';
        } else if (filter.filterType === 'number') {
          obj[filter.paramName] = formValues[filter.paramName];
        } else if (filter.filterType === 'toggle') {
          obj[filter.paramName] = formValues[filter.paramName] || undefined;
        } else if (filter.filterType === 'dateRange') {
          const dateRange = formValues[filter.paramName] as
            | [dayjs.Dayjs, dayjs.Dayjs]
            | null;
          obj[`${filter.paramName}From`] = dateRange
            ? dateRange[0].format('YYYY-MM-DD')
            : null;
          obj[`${filter.paramName}To`] = dateRange
            ? dateRange[1].format('YYYY-MM-DD')
            : null;
        }
      }

      const qp = searchQueryToQueryParams(
        pageNo,
        pageSize,
        dynamicFilters,
        obj as SearchQuery
      );

      const encodedText = encodeURIComponent(searchQuery?.text ?? '').replace(
        /%20/g,
        '+'
      );
      history.push({
        pathname: generatePath(match.path, {
          query: encodedText ? encodedText : undefined,
        }),
        search: qp.toString(),
      });
      onClose();
    },
    [
      pageNo,
      pageSize,
      dynamicFilters,
      searchQuery,
      history,
      match.path,
      onClose,
    ]
  );

  return (
    <Form
      colon={false}
      initialValues={defaultSearch}
      layout="vertical"
      onFinish={submit}
      form={form}
    >
      <Form.Item
        label="Log Names"
        name="logNames"
        style={{ flexBasis: '46px' }}
      >
        <Select
          mode="tags"
          allowClear
          style={{ minWidth: 200 }}
          tokenSeparators={[',', ' ']}
        />
      </Form.Item>

      {dynamicFilters.map((filter) => (
        <FilterItem
          key={'dynamic-filter-' + filter.paramName}
          filter={filter}
          form={form}
        />
      ))}
    </Form>
  );
};
